// Group G2's own words (scorecard and decision): the ones the shared label module does not carry. Each quotes the line
// of his glossary or of the vendored design-system excerpts it comes from; labels.test.mjs checks every quote, that
// the text sits inside it, and that a word still waiting for his confirmation is marked pending.

import { defineLabels, template } from "../labels";

const SCALE_QUOTE = "**Rating** (5 → 1 with the framework's descriptors · *Unassessed*)";
/** Q-E15: the rating scale's sixth option, his word from BRD §3.17, waits for him as a label. */
const UNASSESSED = "*Unassessed* as the sixth option of the rating scale";
const EVIDENCE = "*Evidence* · *Rationale*";
const ROLL = "*Attendance roll* · *Roll Call* · *Required* · *Missing attendee* · *No vote* · *Oversight*";
const RANKED = "*Alternates* · *Active until primary accepts*";
const AGREE = "*HM agreement* · *Recruiter agreement*";
const ACK = "*Accept* · *Decline* · *Acknowledgment Pending*";
const RECO_WORDS = "Alternate to offer · Close · Advance · Hold · Pass · Withdrawn (Step 8)";
/** DS #57's lane words: his *Skill*, which a skill's number follows until it carries his name (asked.ts, Q-E15). */
const SKILL_WORDS = "*Interviewer* · the *virtual / onsite / hybrid* `seg` (no label) · *Executive participation · ROOTS · Skill · Load*";

/** The scorecard's words: the scale, its group name, the recommendation, evidence and rationale. V1's *Not assessed*
 * option reads *Unassessed* (DS §3.4), pending Q-E15. */
export const SCORECARD_LABELS = /* @__PURE__ */ defineLabels({
  rating: { text: "Rating" },
  notAssessed: { text: "Unassessed" },
  recommendation: { text: "Recommendation" },
  evidence: { text: "Evidence" },
  rationale: { text: "Rationale" },
  level: { text: "Level" },
});

/**
 * #26: the one band of the legend, kept in the library and not drawn (DS #26, §3.4: his text gives no score band;
 * averages are Q-E27). The quote is the V1 band the library keeps.
 */
export const HIRE_BAND_LABELS = /* @__PURE__ */ defineLabels({
  hireRange: { text: "4.0 – 5.0" },
});

/** #92 the attendance roll, #93 the ranked alternates, #94 the agreement rows, #97 the acknowledgment pair. */
export const DEBRIEF_ROOM_LABELS = /* @__PURE__ */ defineLabels({
  missingAttendee: { text: "Missing attendee" },
  noVote: { text: "No vote" },
  oversight: { text: "Oversight" },
  activeUntilPrimaryAccepts: { text: "Active until primary accepts" },
  hmAgreement: { text: "HM agreement" },
  recruiterAgreement: { text: "Recruiter agreement" },
  acknowledgmentPending: { text: "Acknowledgment Pending" },
});

/** #25: the decline next paths beside his outcomes, and the blocked tile's sentence (Q-E15, pending). */
export const RECO_LABELS = /* @__PURE__ */ defineLabels({
  alternateToOffer: { text: "Alternate to offer" },
  close: { text: "Close" },
  vpRuleApplied: { text: "VP+ rule applied" },
});

/** #91: an assigned skill's column when the caller has no skill name: his *Skill* and its number, pending (Q-E15). */
export const SCORE_TEMPLATES = {
  skill: /* @__PURE__ */ template({ text: "Skill {n}" }),
} as const;

// ---- B14: the Corporate Interview Playbook's pages (DC-244, DC-245, DC-246, DC-247; his 13 Oct playbook) ---------------

const PLAYBOOK_WORDS = "GLOSSARY rows, each quoting new.txt or the PNGs (his words; 13 Oct)";
const AVERAGE_BOX = "the average box \"x.xx / 5\" + \"from n of 5 values\"";

/** The words of the playbook's value, skill and recommendation pages that the shared components draw (DC-247). The
 *  page content itself (standard, questions, anchors, rubric lines) is the pinned guide's configuration. */
export const PLAYBOOK_LABELS = /* @__PURE__ */ defineLabels({
  standardAssessed: { text: "Standard assessed" },
  behavioralQuestions: { text: "Behavioral interview questions" },
  gradedScoreRating: { text: "Graded score rating (select one)" },
  requiredObservations: { text: "Required observations / evidence" },
  strongAnchor: { text: "5 = Strong" },
  weakAnchor: { text: "1 = Weak" },
  whatStrongLooksLike: { text: "What strong performance looks like" },
  strongObservations: { text: "Strong observations" },
  gapsDetected: { text: "Gaps detected" },
  notes: { text: "Notes" },
  scoreGrade: { text: "Score grade" },
  scoringSummaryRubric: { text: "Scoring summary & rubric" },
  averageCoreScore: { text: "Average core score" },
  suggestedRole: { text: "Suggested role" },
  why: { text: "Why?" },
});

/** The average box (DC-245): "x.xx / 5", and "from n of 5 values" when the card scores fewer than five. */
export const PLAYBOOK_TEMPLATES = {
  outOfFive: /* @__PURE__ */ template({ text: "{average} / 5" }),
  fromValues: /* @__PURE__ */ template({ text: "from {n} of 5 values" }),
} as const;
